import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Download, Smartphone, X, Share, Plus } from 'lucide-react';
import { usePortalPWA } from '@/hooks/usePortalPWA';
import { useOrganizationBranding } from '@/hooks/useOrganizationBranding';

interface PortalInstallPromptProps {
  variant?: 'banner' | 'card';
  className?: string;
}

export function PortalInstallPrompt({ variant = 'banner', className = '' }: PortalInstallPromptProps) {
  const { canInstall, promptInstall, dismissInstall, isIOS, isMobile, isInstalled, isPWACapable } = usePortalPWA();
  const { name: orgName } = useOrganizationBranding();
  const [isInstalling, setIsInstalling] = useState(false);

  // Always log PWA state for debugging
  console.log('[PortalInstallPrompt] State:', {
    isInstalled, 
    isMobile,
    canInstall,
    isIOS,
    isPWACapable,
    organizationName: orgName
  });

  // Show if PWA capable and not installed (remove mobile-only restriction)
  if (isInstalled || !canInstall) {
    return null;
  }

  const handleInstall = async () => {
    setIsInstalling(true);
    const success = await promptInstall();
    setIsInstalling(false);
    
    if (!success && !isIOS) {
      console.log('Install was cancelled or failed - showing manual instructions');
      // Could show a toast with manual install instructions here
    }
  };

  // Manual install instructions for browsers without beforeinstallprompt
  const showManualInstructions = !isIOS && isPWACapable && !canInstall;

  if (showManualInstructions) {
    return (
      <Card className={`${className} border-primary/20 bg-primary/5`}>
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <div className="p-2 rounded-full bg-primary/10">
                <Download className="h-4 w-4 text-primary" />
              </div>
              <div>
                <CardTitle className="text-sm">Install {orgName} Portal</CardTitle>
                <CardDescription className="text-xs">
                  Add to your device for quick access
                </CardDescription>
              </div>
            </div>
            <Button variant="ghost" size="sm" onClick={dismissInstall} className="h-8 w-8 p-0">
              <X className="h-4 w-4" />
            </Button>
          </div>
        </CardHeader>
        <CardContent className="pt-0">
          <Alert className="border-primary/20">
            <AlertDescription>
              <div className="space-y-2">
                <p className="font-medium text-sm">To install this app:</p>
                <ol className="text-xs space-y-1 ml-4">
                  <li>• Look for an install icon in your browser's address bar</li>
                  <li>• Or check your browser's menu for "Install app" option</li>
                  <li>• On desktop: Chrome/Edge menu → "Install {orgName} Portal"</li>
                </ol>
              </div>
            </AlertDescription>
          </Alert>
        </CardContent>
      </Card>
    );
  }

  if (isIOS) {
    return (
      <Card className={`${className} border-primary/20 bg-primary/5`}>
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <div className="p-2 rounded-full bg-primary/10">
                <Smartphone className="h-4 w-4 text-primary" />
              </div>
              <div>
                <CardTitle className="text-sm">Install {orgName} Portal</CardTitle>
                <CardDescription className="text-xs">
                  Quick access to your bridal journey
                </CardDescription>
              </div>
            </div>
            <Button variant="ghost" size="sm" onClick={dismissInstall} className="h-8 w-8 p-0">
              <X className="h-4 w-4" />
            </Button>
          </div>
        </CardHeader>
        <CardContent className="pt-0">
          <Alert className="border-primary/20">
            <Share className="h-4 w-4" />
            <AlertDescription>
              <div className="space-y-2">
                <p className="font-medium text-sm">To install on iPhone:</p>
                <ol className="text-xs space-y-1 ml-4">
                  <li className="flex items-center gap-2">
                    <Share className="h-3 w-3" />
                    Tap the Share button below
                  </li>
                  <li className="flex items-center gap-2">
                    <Plus className="h-3 w-3" />
                    Scroll and tap "Add to Home Screen"
                  </li>
                  <li>Tap "Add" to install</li>
                </ol>
              </div>
            </AlertDescription>
          </Alert>
        </CardContent>
      </Card>
    );
  }

  if (variant === 'banner') {
    return (
      <div className={`${className} bg-primary/5 border-primary/20 border rounded-lg p-3 m-4`}>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-full bg-primary/10">
              <Download className="h-4 w-4 text-primary" />
            </div>
            <div>
              <p className="font-medium text-sm">Install {orgName} Portal</p>
              <p className="text-xs text-muted-foreground">Quick access to your bridal journey</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button 
              size="sm" 
              onClick={handleInstall} 
              disabled={isInstalling}
              className="h-8 px-3 text-xs"
            >
              {isInstalling ? 'Installing...' : 'Install'}
            </Button>
            <Button variant="ghost" size="sm" onClick={dismissInstall} className="h-8 w-8 p-0">
              <X className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <Card className={`${className} border-primary/20 bg-primary/5`}>
      <CardHeader>
        <div className="flex items-center justify-between">
          <CardTitle className="flex items-center gap-2 text-base">
            <Download className="h-5 w-5" />
            Install {orgName} Portal
          </CardTitle>
          <Button variant="ghost" size="sm" onClick={dismissInstall} className="h-8 w-8 p-0">
            <X className="h-4 w-4" />
          </Button>
        </div>
        <CardDescription>
          Get the full app experience with offline access and push notifications
        </CardDescription>
      </CardHeader>
      <CardContent>
        <Button onClick={handleInstall} disabled={isInstalling} className="w-full">
          {isInstalling ? 'Installing...' : 'Install App'}
        </Button>
      </CardContent>
    </Card>
  );
}